import { useEffect, useMemo, useRef, useState } from 'react';

import { useMapContextRedistributor } from 'app/contexts/MapContextRedistributor/mapContextRedistributorProvider';

import {
  GetCustomerAggregatePins,
  GetCustomerAggregatePinsVariables,
  TGTAggregationInput
} from 'app/graphql/generated/graphqlApolloTypes';
import { handleError } from 'app/graphql/handleError';
import { useGetCustomerAggregatePins } from 'app/graphql/queries/getCustomerAggregatePins';

import useShowToast from 'app/hooks/useShowToast';
import { useSplitPageSize } from 'app/hooks/useSplitPageSize';

import { AggregatePin } from 'app/models';

import { asyncRequestQueue } from 'utils/asyncRequestQueue';
import { startPerformanceTimer } from 'utils/helpers/analyticsReporter';
import { CompressionUtil } from 'utils/helpers/CompressionUtil';
import { formatMessage } from 'utils/messages/utils';

import { usePinsWarningThreshold } from './usePinsWarningThreshold';

export type CustomerAggregatesFeatures = {
  customerPins: AggregatePin[];
  loading: boolean;
  refetchAllPins: () => void;
  warning: AccountPinWarning | null;
};
export const useCustomerAggregatesFeatures = (measureId: number): CustomerAggregatesFeatures => {
  const [loading, setLoading] = useState(false);
  const [warning, setWarning] = useState<AccountPinWarning | null>(null);
  const { selectedQuotaComponentId, selectedBattleCardId, selectedTerritoryGroupTypeId, accountSourceFilter } =
    useMapContextRedistributor();
  const [customerPins, setCustomerPins] = useState<AggregatePin[]>([]);

  const showToast = useShowToast();

  const maxCount = usePinsWarningThreshold();
  const pageSize = useSplitPageSize('getCustomerAggregatePins');
  const parallelCount = useSplitPageSize('getCustomerAggregatePinsParallelCount');

  const refetchAllPins = () => {
    setCustomerPins([]);
    fetchAllPinsAndSaveToState();
  };

  const input: TGTAggregationInput = useMemo(() => {
    if (!selectedBattleCardId || !selectedQuotaComponentId || !selectedTerritoryGroupTypeId || !measureId) return null;
    return {
      battlecardId: +selectedBattleCardId,
      quotaComponentId: selectedQuotaComponentId,
      territoryGroupId: selectedTerritoryGroupTypeId,
      measureId
    };
  }, [selectedBattleCardId, selectedQuotaComponentId, selectedTerritoryGroupTypeId, measureId]);

  const { fetchMore, refetch } = useGetCustomerAggregatePins({
    fetchPolicy: 'network-only',
    skip: true
  });

  useEffect(() => {
    setCustomerPins([]);
  }, [input]);

  const canonicalRequestId = useRef(0);

  const fetchAllPinsAndSaveToState = () => {
    canonicalRequestId.current += 1;
    const localRequestId = canonicalRequestId.current;
    setLoading(true);
    setWarning(null);

    getAllPinPages({
      input,
      pageSize,
      maxCount,
      parallelCount,
      getFirstPage: refetch,
      getAdditionalPage: fetchMore
    })
      .then(({ allCustomerPins }) => {
        if (localRequestId !== canonicalRequestId.current) return;
        setCustomerPins(allCustomerPins);
        setLoading(false);
      })
      .catch((error: unknown) => {
        if (localRequestId !== canonicalRequestId.current) return;
        handleError(null, <Error>error);
        showToast(formatMessage('CUSTOMER_PINS_ERROR'), 'danger');
        setWarning(isExceedsMaximumCause(error) ? error.cause : { kind: 'Error', maxCount });
        setCustomerPins([]);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!input) {
      return () => null;
    }
    fetchAllPinsAndSaveToState();
    return () => {
      canonicalRequestId.current += 1;
    };
  }, [input, accountSourceFilter]);

  return {
    customerPins,
    loading,
    warning,
    refetchAllPins
  };
};

type AccountPinExceedsMaximumWarning = {
  kind: 'ExceedsMaximum';
  count: number;
  maxCount: number;
};

type AccountPinErrorWarning = {
  kind: 'Error';
  maxCount: number;
};
export type AccountPinWarning = AccountPinExceedsMaximumWarning | AccountPinErrorWarning;

type GetAllPinPagesInput = {
  input: TGTAggregationInput;
  pageSize: number;
  parallelCount: number;
  maxCount: number | null;
  getFirstPage: (variables?: Partial<GetCustomerAggregatePinsVariables>) => Promise<{ data: GetCustomerAggregatePins }>;
  getAdditionalPage: (input: {
    variables?: Partial<GetCustomerAggregatePinsVariables>;
  }) => Promise<{ data: GetCustomerAggregatePins }>;
};

const getAllPinPages = async ({
  input,
  pageSize,
  parallelCount,
  maxCount,
  getFirstPage,
  getAdditionalPage
}: GetAllPinPagesInput): Promise<{ allCustomerPins: AggregatePin[]; totalRowCount: number }> => {
  const timer = startPerformanceTimer('getAllPinPages');
  const paginationInput = { startRow: 1, endRow: pageSize };
  const firstPage = (await getFirstPage({ input: { ...input, ...paginationInput }, includeCount: true })).data;

  const compressedFirstPage = firstPage.getTGTAggregate.pinsCompressed;
  const decompressedFirstPage = CompressionUtil.decompress(compressedFirstPage);

  const totalRowCount = firstPage.getTGTAggregate.numberOfAccounts ?? 0;
  if (maxCount && totalRowCount > maxCount)
    throw new Error(`Too many pins to load`, {
      cause: { kind: 'ExceedsMaximum', count: totalRowCount, maxCount } satisfies AccountPinExceedsMaximumWarning
    });

  const totalPageCount = Math.ceil(totalRowCount / pageSize);
  const otherPageInputs = new Array<{ variables: GetCustomerAggregatePinsVariables }>();
  for (let pageIndex = 1; pageIndex < totalPageCount; pageIndex++) {
    otherPageInputs.push({
      variables: {
        input: {
          ...input,
          startRow: pageSize * pageIndex + 1,
          endRow: pageSize * (pageIndex + 1)
        },
        includeCount: false
      }
    });
  }

  const otherPages = await asyncRequestQueue(parallelCount, otherPageInputs, (input) =>
    getAdditionalPage(input).then(({ data }) => CompressionUtil.decompress(data.getTGTAggregate.pinsCompressed))
  );

  const customerPinPages = [decompressedFirstPage, ...otherPages];
  const allCustomerPins = customerPinPages.flat();
  timer.stop({
    count: allCustomerPins.length,
    pageCount: customerPinPages.length
  });
  return { allCustomerPins, totalRowCount };
};

const isExceedsMaximumCause = (error: unknown): error is { cause: AccountPinExceedsMaximumWarning } =>
  hasOwnField(error, 'cause') && hasOwnField(error.cause, 'kind') && error.cause.kind === 'ExceedsMaximum';

const hasOwnField = <F extends string>(target: unknown, field: F): target is Record<F, unknown> =>
  target && typeof target === 'object' && Object.hasOwn(target, field);
